import { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import {
  fetchRecordings,
  deleteRecording,
  transcribeRecordingById,
  fetchLexicon,
  deleteLexiconEntry,
  fetchVoices,
  fetchJournal,
  nameSpeakers,
  speakerAudioUrl,
  type Recording,
  type Journal,
  type LexiconEntry,
  type SessionSpeaker,
  type Voice,
} from "./api.js";
import { useRecording } from "./recording.js";

function fmtDate(iso: string): string {
  try {
    return new Date(iso).toLocaleString("fr-FR", {
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function fmtDur(ms: number): string {
  const s = Math.round(ms / 1000);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}

function mmss(ms: number): string {
  const s = Math.round(ms / 1000);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}

/**
 * Le journal d'une session : toute la conversation, dans l'ordre.
 *
 * Les vraies sessions du 15/08/2026 ont montré que regarder une photo déclenche des
 * souvenirs qui ne parlent pas d'elle. Les rattacher de force serait une interprétation ;
 * ne pas les rattacher les faisait disparaître — 52 % des souvenirs, 41 % des mots.
 *
 * Ici la session est l'objet, et ce qu'on regardait n'est qu'un **contexte** affiché à côté :
 * « pendant qu'on regardait ça », un fait, plutôt que « à propos de ça », une supposition.
 */
function SessionJournal({ recordingId }: { recordingId: string }) {
  const [journal, setJournal] = useState<Journal | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);

  useEffect(() => {
    fetchJournal(recordingId)
      .then(setJournal)
      .catch((e: unknown) => setErreur(e instanceof Error ? e.message : String(e)));
  }, [recordingId]);

  if (erreur) return <p className="modal__muted">Journal indisponible : {erreur}</p>;
  if (!journal) return <p className="modal__muted">Chargement du journal…</p>;
  if (journal.passages.length === 0) {
    return <p className="modal__muted">Rien n'a été transcrit pour cette session.</p>;
  }

  const nomVoix = new Map(journal.recording.speakers.map((v) => [v.label, v.name]));
  return (
    <div className="jrnl" data-testid="session-journal">
      <p className="jrnl__head">
        {journal.passages.length} passages
        {journal.sansContexte > 0 && (
          <>
            {" · "}
            <span className="jrnl__hors">
              {journal.sansContexte} dits hors d'une photo — conservés ici
            </span>
          </>
        )}
      </p>
      <ol className="jrnl__list">
        {journal.passages.map((p) => (
          <li key={p.id} className="jrnl__p" data-testid="journal-passage">
            <span className="jrnl__t">{mmss(p.tStartMs)}</span>
            <span className="jrnl__qui">
              {p.speakerName ??
                (p.speakerLabel !== null ? (nomVoix.get(p.speakerLabel) ?? `voix ${p.speakerLabel}`) : "—")}
            </span>
            <span className="jrnl__texte">
              {p.text}
              {(p.photoId ?? p.eventTitle) && (
                <span className="jrnl__ctx">
                  {p.eventTitle ? `en regardant « ${p.eventTitle} »` : "en regardant une photo"}
                </span>
              )}
            </span>
          </li>
        ))}
      </ol>
    </div>
  );
}

/**
 * Les voix d'une session, à nommer.
 *
 * Le modèle sépare les voix mais ne dit **jamais** « inconnu » : mesuré le 28/07/2026, une
 * voix inconnue est attribuée à quelqu'un d'autre avec 0,90 de confiance (DESIGN §11). Le
 * nom vient donc toujours d'un humain — on écoute l'extrait le plus long de chaque voix, on
 * tape le prénom, et ça se propage à tous les commentaires de la session. Laisser vide
 * n'attribue rien, ce qui vaut toujours mieux qu'un nom faux.
 */
function SessionVoices({
  recording,
  voices,
  onNamed,
}: {
  recording: Recording;
  voices: Voice[];
  onNamed: (speakers: SessionSpeaker[]) => void;
}) {
  const speakers = recording.speakers ?? [];
  const [noms, setNoms] = useState<Record<number, string>>(() =>
    Object.fromEntries(speakers.map((s) => [s.label, s.name ?? ""])),
  );
  const [busy, setBusy] = useState(false);
  const [joue, setJoue] = useState<number | null>(null);

  if (speakers.length === 0) return null;

  const ecouter = (label: number): void => {
    const audio = new Audio(speakerAudioUrl(recording.id, label));
    setJoue(label);
    audio.onended = () => setJoue(null);
    audio.onerror = () => setJoue(null);
    void audio.play().catch(() => setJoue(null));
  };

  const enregistrer = async (): Promise<void> => {
    setBusy(true);
    try {
      onNamed(
        await nameSpeakers(
          recording.id,
          speakers.map((s) => ({ label: s.label, name: noms[s.label] ?? "" })),
        ),
      );
    } finally {
      setBusy(false);
    }
  };

  const change = speakers.some((s) => (noms[s.label] ?? "") !== (s.name ?? ""));

  return (
    <div className="voices" data-testid="session-voices">
      <p className="voices__head">
        {speakers.length} voix détectée{speakers.length > 1 ? "s" : ""} — écoute et nomme
      </p>
      <ul className="voices__list">
        {speakers.map((s) => (
          <li key={s.label} className="voice" data-testid="voice-row">
            <button
              className="voice__play"
              onClick={() => ecouter(s.label)}
              disabled={s.sampleSeq === null || s.sampleSeq === undefined}
              aria-label={`Écouter la voix ${s.label}`}
              data-testid="voice-play"
            >
              {joue === s.label ? "■" : "▶"}
            </button>
            <span className="voice__desc" title={s.voice}>{s.voice}</span>
            <input
              className="voice__name"
              list="voices-connues"
              placeholder="Prénom…"
              value={noms[s.label] ?? ""}
              onChange={(e) => setNoms((n) => ({ ...n, [s.label]: e.target.value }))}
              onKeyDown={(e) => {
                if (e.key === "Enter" && change && !busy) void enregistrer();
              }}
              data-testid="voice-name"
            />
          </li>
        ))}
      </ul>
      <datalist id="voices-connues">
        {voices.map((v) => (
          <option key={v.id} value={v.name} />
        ))}
      </datalist>
      {change && (
        <button
          className="voices__save"
          disabled={busy}
          onClick={() => void enregistrer()}
          data-testid="voices-save"
        >
          {busy ? "Enregistrement…" : "Enregistrer les noms"}
        </button>
      )}
    </div>
  );
}

/** Panneau de gestion des sessions (ouvert depuis le menu utilisateur, propriétaire). */
export function SessionsPanel({ onClose }: { onClose: () => void }) {
  const { refreshData } = useRecording();
  const [recordings, setRecordings] = useState<Recording[] | null>(null);
  const [confirmId, setConfirmId] = useState<string | null>(null);
  const [retransId, setRetransId] = useState<string | null>(null);
  const [journalId, setJournalId] = useState<string | null>(null);
  /** Action en cours : on retient aussi laquelle, pour le libellé d'attente. */
  const [busy, setBusy] = useState<{ id: string; kind: "transcribe" | "delete" } | null>(null);

  const [lexicon, setLexicon] = useState<LexiconEntry[] | null>(null);
  const [voices, setVoices] = useState<Voice[]>([]);

  const load = useCallback(() => {
    fetchRecordings()
      .then(setRecordings)
      .catch(() => setRecordings([]));
    fetchLexicon()
      .then(setLexicon)
      .catch(() => setLexicon([]));
    fetchVoices()
      .then(setVoices)
      .catch(() => setVoices([]));
  }, []);

  useEffect(() => load(), [load]);

  // Les deux actions durent (Gemini, GCS) : on quitte la confirmation tout de suite
  // et on affiche l'attente, sinon le panneau paraît figé pendant 10 à 30 secondes.
  const remove = async (r: Recording) => {
    setConfirmId(null);
    setBusy({ id: r.id, kind: "delete" });
    try {
      await deleteRecording(r.id);
      refreshData(); // met à jour la galerie (souvenirs retirés)
    } finally {
      setBusy(null);
      load();
    }
  };

  const transcribe = async (r: Recording) => {
    setRetransId(null);
    setBusy({ id: r.id, kind: "transcribe" });
    // Le serveur passe le statut à « transcribing » : on l'affiche sans attendre.
    setRecordings((rs) => rs?.map((x) => (x.id === r.id ? { ...x, status: "transcribing" } : x)) ?? rs);
    try {
      await transcribeRecordingById(r.id);
      refreshData();
    } finally {
      setBusy(null);
      load();
    }
  };

  const nbEvents = (r: Recording) =>
    new Set(r.timeline.map((t) => t.eventId).filter(Boolean)).size;

  return createPortal(
    <div className="modal" data-testid="sessions-panel" onClick={onClose}>
      <div className="modal__card modal__card--wide" onClick={(e) => e.stopPropagation()}>
        <header className="modal__head">
          <h3 className="modal__title">Sessions d'enregistrement</h3>
          <button className="modal__close" onClick={onClose} aria-label="Fermer">×</button>
        </header>

        {!recordings ? (
          <p className="modal__muted">Chargement…</p>
        ) : recordings.length === 0 ? (
          <p className="modal__muted">Aucune session enregistrée.</p>
        ) : (
          <ul className="sessions__list">
            {recordings.map((r) => (
              <li key={r.id} className="session" data-testid="session-row">
                <div className="session__info">
                  <span className="session__date">{fmtDate(r.startedAt)}</span>
                  <span className="session__sub">
                    {fmtDur(r.durationMs)} · {nbEvents(r)} évén. · {r.status}
                  </span>
                </div>
                {busy?.id === r.id ? (
                  <span className="session__busy" data-testid="session-busy">
                    <span className="session__dot" />
                    {busy.kind === "transcribe" ? "Transcription en cours…" : "Suppression…"}
                  </span>
                ) : confirmId === r.id ? (
                  <span className="session__confirm">
                    Supprimer l'audio + transcript ?
                    <button
                      className="session__yes"
                      onClick={() => void remove(r)}
                    >
                      Oui
                    </button>
                    <button className="session__no" onClick={() => setConfirmId(null)}>
                      Non
                    </button>
                  </span>
                ) : retransId === r.id ? (
                  <span className="session__confirm">
                    Refaire la transcription ? Les commentaires actuels seront remplacés.
                    <button
                      className="session__yes"
                      onClick={() => void transcribe(r)}
                    >
                      Oui
                    </button>
                    <button className="session__no" onClick={() => setRetransId(null)}>
                      Non
                    </button>
                  </span>
                ) : (
                  <span className="session__actions">
                    {/* Toujours proposable : c'est le seul recours quand le pipeline de
                        transcription a été corrigé après coup. L'audio est conservé. */}
                    <button
                      className="session__transcribe"
                      data-testid="session-journal-open"
                      onClick={() => setJournalId(journalId === r.id ? null : r.id)}
                    >
                      {journalId === r.id ? "Masquer" : "Journal"}
                    </button>
                    <button
                      className="session__transcribe"
                      data-testid="session-transcribe"
                      onClick={() =>
                        r.status === "transcribed" ? setRetransId(r.id) : void transcribe(r)
                      }
                    >
                      {r.status === "transcribed" ? "Retranscrire" : "Transcrire"}
                    </button>
                    <button
                      className="session__del"
                      data-testid="session-del"
                      onClick={() => setConfirmId(r.id)}
                    >
                      Supprimer
                    </button>
                  </span>
                )}
                {journalId === r.id && <SessionJournal recordingId={r.id} />}
                <SessionVoices
                  recording={r}
                  voices={voices}
                  onNamed={(speakers) => {
                    setRecordings((rs) =>
                      (rs ?? []).map((x) => (x.id === r.id ? { ...x, speakers } : x)),
                    );
                    fetchVoices().then(setVoices).catch(() => {});
                    refreshData();
                  }}
                />
              </li>
            ))}
          </ul>
        )}

        {/* Ce que les corrections ont appris. Visible pour être corrigible : un nom
            appris à tort se glisserait dans toutes les transcriptions suivantes. */}
        {lexicon && lexicon.length > 0 && (
          <section className="lex" data-testid="lexicon">
            <h4 className="lex__title">
              Noms appris <span className="gallery__count">{lexicon.length}</span>
            </h4>
            <p className="lex__hint">
              Retenus depuis tes corrections et transmis au modèle à chaque nouvelle
              transcription. Retire ceux qui sont faux.
            </p>
            <ul className="lex__list">
              {lexicon.map((e) => (
                <li key={e.id} className="lex__item" data-testid="lex-item">
                  <span className="lex__name">{e.name}</span>
                  {e.heard.length > 0 && (
                    <span className="lex__heard">entendu « {e.heard.join(" », « ")} »</span>
                  )}
                  <button
                    className="lex__del"
                    aria-label={`Retirer ${e.name}`}
                    onClick={() => {
                      void deleteLexiconEntry(e.id).then(load);
                    }}
                  >
                    ×
                  </button>
                </li>
              ))}
            </ul>
          </section>
        )}
      </div>
    </div>,
    document.body,
  );
}
